import { memo, useCallback, useMemo, useState } from 'react';

import { useResponsiveScale } from '../../hooks/useResponsiveRem';
import { DeckIcon } from '../../icons/custom/DeckIcon';
import { Ellipsis } from '../../icons/lucide/Ellipsis';
import { EllipsisVertical } from '../../icons/lucide/EllipsisVertical';
import { Pressable, type PressEvent } from '../../primitives/Pressable';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { DARK_LAYER1 } from '../../styles/elevation';
import { useTheme } from '../../theme';
import { cardActionsA11yProps } from '../../utils/cardActionsA11y';
import { cn } from '../../utils/cn';
import { tintHex, withAlpha } from '../../utils/color';
import { subTestID } from '../../utils/testId';
import { Button } from '../Button';
import { CardBodyPanel } from '../cards/CardBodyPanel';
import { CardChips } from '../cards/CardChips';
import { CardImage } from '../cards/CardImage';
import { CARD_NATIVE_SHADOW } from '../cards/cardShadows';
import { CardTagLabel } from '../cards/CardTagLabel';
import { CardTodoBadge } from '../cards/CardTodoBadge';
import { COMPACT_TITLE_PIN, compactPanelRows } from '../cards/cardVariants';
import { MarqueeLabel } from '../MarqueeLabel';
import type { DeckCardProps } from './DeckCard.types';
import {
  CARD_SURFACE_ALPHA,
  deckCardBodyVariants,
  deckCardBorderClass,
  deckCardCoverVariants,
  deckCardTitleVariants,
  deckCardVariants,
} from './DeckCard.variants';

/** The design's sample deck blue (Figma variable `Deck/Deck-blue`). */
const DEFAULT_DECK_COLOR = '#60a5fa';

/**
 * Cross-platform DeckCard — the design-system card for a deck ("Study set"
 * in Figma). Renders identically on web and React Native.
 *
 * Mirrors the Figma "Flashka Design System" file `YZK9r3wDhvw1pY60KlVEIj`,
 * "Cards Updated" frame (node `3153:44267`), `Type=Study set`:
 * grid 180×180 tile (cover image or colour-tinted emoji tile) and full-width
 * list row (deck glyph + title + chips, optional trailing thumbnail).
 *
 * @example
 * ```tsx
 * import { DeckCard } from '@flashka/shared/ui';
 *
 * <DeckCard
 *   view="grid"
 *   title="Biology 101"
 *   emoji="🧬"
 *   color="#9ae600"
 *   badges={[
 *     { count: 24, icon: Layers, label: t('deck.flashcards', { count: 24 }) },
 *     { count: 4, icon: ListChecks, label: t('deck.quizzes', { count: 4 }) },
 *     { count: 1, icon: Speech, label: t('deck.oralExams', { count: 1 }) },
 *     { count: 3, icon: ClipboardList, label: t('deck.caseStudies', { count: 3 }) },
 *   ]}
 *   onPress={openDeck}
 *   onActionsPress={openDeckMenu}
 *   actionsLabel="Deck actions"
 * />
 * ```
 */
function DeckCardImpl({
  view,
  size,
  title,
  image,
  media = true,
  emoji = '🏎️',
  color = DEFAULT_DECK_COLOR,
  badges,
  emptyLabel,
  todoCount,
  todoLabel,
  todoTooltip,
  titleTrailing,
  coverOverlay,
  onPress,
  onActionsPress,
  actionsLabel,
  actionsSlot,
  disabled = false,
  className,
  accessibilityLabel,
  actionsTestID,
  testID,
  webProps,
  nativeProps,
}: DeckCardProps) {
  const resolvedView = view ?? 'grid';
  // Grid subvariant: `compact` (180, the mobile tile) or `expanded` (225, the
  // web dashboard tile). List ignores it.
  const resolvedSize = size ?? 'compact';
  // 1 on web/phones; tablets render every rem token 1.25× larger
  // (ResponsiveScaleProvider) — the expanded card's px-fixed geometry
  // multiplies by the same factor so the card scales COHERENTLY instead of
  // cramming bigger type into a fixed box (see FolderLikeCard's note).
  const scale = useResponsiveScale();
  const { isDark } = useTheme();
  const isExpandedGrid = resolvedSize === 'expanded';
  const gridBoxStyle = useMemo(
    () =>
      isExpandedGrid
        ? { width: Math.round(225 * scale), height: Math.round(225 * scale) }
        : undefined,
    [isExpandedGrid, scale],
  );
  const bodyStyle = useMemo(
    () => (isExpandedGrid ? { height: Math.round(110 * scale) } : undefined),
    [isExpandedGrid, scale],
  );
  const px = (n: number) => Math.round(n * scale);
  // The one scaled inset the memoized cover chips need. A VALUE, not `px(12)`:
  // `px` is rebuilt every render, so a memo listing it would never hold — and
  // this package has no `exhaustive-deps` rule to catch that for us.
  const scaled12 = Math.round(12 * scale);
  // Media is the preferred version, with automatic fallback to no-media when
  // it isn't doable: no source-document preview, the preview failed to load
  // (remembered per-src so a NEW src gets a fresh attempt — Avatar pattern),
  // or the caller opted out via `media={false}`.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showMedia = media && !!image && image !== failedSrc;
  const handleImageError = useCallback(() => setFailedSrc(image ?? null), [image]);
  // The cover's two possible contents, memoized. Identical props at both call
  // sites (the grid tile and the list row's thumbnail), so ONE element serves
  // both — and neither depends on `pressed`, which is the point: the tile's own
  // node re-renders for the lift class, its content does not. The tag label is
  // a `MarqueeLabel` underneath (measuring copy + gradient mask + `useId`), so
  // rebuilding it twice per tap was the most expensive thing a press did.
  const imageNode = useMemo(
    () => (image ? <CardImage src={image} onError={handleImageError} /> : null),
    [image, handleImageError],
  );
  const tagNode = useMemo(
    () => (
      // Emoji / tag label — the shared one every surface a deck's art reaches
      // uses (folder pages, list thumbs), so a tag reads the same in its own
      // card as inside the folder that holds it. A tag may take the tile's
      // second line before it truncates.
      <CardTagLabel
        label={emoji}
        color={color}
        glyphSize={isExpandedGrid ? 36 : 28}
        tagSize={isExpandedGrid ? 36 : 28}
        tagLines={2}
      />
    ),
    [emoji, color, isExpandedGrid],
  );
  // Press = touch's hover: while the card is held down the preview tile
  // lifts, exactly like the web hover (and stays lifted through the hold).
  //
  // The ONLY thing on the card this drives is the cover tile's lift class, and
  // the memoized nodes above are what keep it that cheap: a press re-runs this
  // body, but the title, the chip row, the ⋯ and the glass panel are all
  // element-identical, so React bails out of every one of those subtrees. Two
  // events per tap × every card the finger touches is otherwise a full card
  // rebuild for a 4px translate.
  const [pressed, setPressed] = useState(false);
  const handlePressIn = useCallback(() => setPressed(true), []);
  const handlePressOut = useCallback(() => setPressed(false), []);
  // An Apple Pencil (or trackpad) held over the card lifts it the same way —
  // the native Pressable reports hover for pen/mouse pointers only, never a
  // finger. On web it lands on the same lift the CSS hover already draws.
  const [hovered, setHovered] = useState(false);
  const handleHoverIn = useCallback(() => setHovered(true), []);
  const handleHoverOut = useCallback(() => setHovered(false), []);
  const lifted = pressed || hovered;

  // The card's own surface. An INLINE style rather than the `bg-secondary-50/NN`
  // class it used to be: that alpha utility is runtime-processed, and the view
  // axis flips on a live element every time a screen's grid/list toggle fires —
  // the NativeWind in-place-restyle crash. See `CARD_SURFACE_ALPHA`.
  //
  // LIGHT only. Dark mode puts the card on the elevation ladder's content
  // SURFACE instead (`DARK_LAYER1`, a constant class on the container, both
  // views): the mirrored `secondary-50` sat at the chrome level — darker than
  // the widgets beside it — and a list row at 70% of it read as the bare page.
  // An inline background would beat the class, so dark sets none.
  const surfaceStyle = useMemo(
    () =>
      isDark
        ? undefined
        : {
            backgroundColor: withAlpha(
              brandColor('secondary', '50', false),
              CARD_SURFACE_ALPHA[resolvedView],
            ),
          },
    [isDark, resolvedView],
  );

  // Web shadows come from the `web:shadow-card*` classes; native needs the
  // RN shadow props (NativeWind can't compile box-shadow). Web Pressable
  // swallows nativeProps entirely, so merging unconditionally is safe.
  // The ⋯ reachable to a SCREEN READER. The container below is ONE accessibility
  // element on iOS, which hid the ⋯ entirely; a custom action exposes it without
  // costing the card its own "open this deck" activation. Native-only by
  // construction — the web Pressable discards `nativeProps`, and web already has
  // the ⋯ as a real nested <button> under `as="div"`. Full rationale, and why
  // `accessible={false}` is the wrong fix here, live in the helper.
  const actionsA11y = useMemo(
    () => cardActionsA11yProps({ onActionsPress, actionsLabel, disabled }),
    [onActionsPress, actionsLabel, disabled],
  );

  const mergedNativeProps = useMemo(
    () => ({
      ...nativeProps,
      ...actionsA11y,
      style: [
        CARD_NATIVE_SHADOW[resolvedView],
        surfaceStyle,
        (nativeProps as { style?: unknown } | undefined)?.style,
      ],
    }),
    [nativeProps, actionsA11y, resolvedView, surfaceStyle],
  );

  const isCompactGrid = resolvedView === 'grid' && resolvedSize === 'compact';
  // The compact panel's row geometry, straight from the Figma anatomy —
  // shared with FolderLikeCard so the two cards can't drift apart.
  const {
    title: compactTitleRow,
    chips: compactChipsRow,
    actions: compactActionsSlot,
  } = compactPanelRows('deck');

  // Activity chips (shared across all DS cards) — icon + count, hidden at 0.
  // Grid caps the row at one line; list fits all. DS sizes: list rows and the
  // expanded (1.25×) grid tile use size-`s` chips; the compact tile `xs`.
  const badgeRow = useMemo(
    () => (
      <CardChips
        badges={badges}
        emptyLabel={emptyLabel}
        view={resolvedView}
        badgeSize={resolvedView === 'list' || resolvedSize === 'expanded' ? 's' : 'xs'}
        // Compact grid only: its panel is a fixed 89px, so the chip row must
        // hold its 24px design height at every rem scale (see `pinned`).
        pinned={isCompactGrid}
        // List rows lead the row with the to-do chip; grid tiles show it in
        // the corner instead. Either way it promotes the flashcards chip.
        todoCount={todoCount}
        todoLabel={todoLabel}
      />
    ),
    [badges, emptyLabel, isCompactGrid, resolvedSize, resolvedView, todoCount, todoLabel],
  );

  // Trailing ⋯ (grid, 32px / 16px icon) or ⋮ (list, 28px / 14px icon) —
  // Figma's transparent "Icon Button" = the shared ghost Button. Stop the
  // press from bubbling into the card's own onPress on web. `actionsSlot`
  // replaces the button when the caller's menu library must own the trigger
  // element (web Radix `DropdownMenu.Trigger asChild`).
  const actions = useMemo(
    () =>
      actionsSlot ??
      (onActionsPress ? (
        <Button
          variant="ghost"
          size={resolvedView === 'grid' ? 'xs' : 'xxs'}
          icon={resolvedView === 'grid' ? Ellipsis : EllipsisVertical}
          disabled={disabled}
          accessibilityLabel={actionsLabel}
          testID={actionsTestID ?? subTestID(testID, 'menu')}
          onPress={(event: PressEvent) => {
            event?.stopPropagation?.();
            onActionsPress(event);
          }}
        />
      ) : null),
    [
      actionsSlot,
      onActionsPress,
      resolvedView,
      disabled,
      actionsLabel,
      actionsTestID,
      testID,
    ],
  );

  // Shared MarqueeLabel (fluid mode): the title clamps to its box with a
  // trailing FADE rather than an `…` (designer, 2026-08) — the ramp says
  // "there's more" without spending a character saying it, so the same box
  // shows more of the name. On web a hover marquee still reveals the rest.
  const titleNode = useMemo(
    () => (
      <MarqueeLabel
        overflow="fade"
        className={cn(
          deckCardTitleVariants({ view: resolvedView }),
          // The compact tile is a fixed-px box, so its title holds the design
          // type on native too (see COMPACT_TITLE_PIN).
          isCompactGrid && COMPACT_TITLE_PIN,
          // List: content-width (w-auto overrides fluid's web w-full) +
          // shrink, so `titleTrailing` sits right AFTER the title instead of
          // being pushed to the row's end; the clamp still engages when
          // the row runs out of space.
          resolvedView === 'list' && 'w-auto shrink',
        )}
        label={title}
        testID={subTestID(testID, 'title')}
      />
    ),
    [isCompactGrid, resolvedView, title, testID],
  );

  // GRID art, derived here rather than in the grid branch below: the branch
  // sits under an early `return`, and a hook cannot. Cheap enough to derive for
  // a list row that never reads it (one hex mix, memoized), and worth it —
  // these are `style` objects, which React Native diffs BY REFERENCE, so a
  // fresh literal per render re-sent the tile and the glass panel to native on
  // every press.
  //
  // The no-media emoji tile's bg derives from the deck colour (30% toward white
  // ≈ the -300 shade; toward black in dark — see `tintHex`); the border is the
  // deck colour ITSELF — callers pass the palette -400 shade, and anything
  // lighter reads as no border at all. Inline style because the colour is an
  // arbitrary runtime hex.
  const tileTint = useMemo(
    () =>
      showMedia
        ? undefined
        : {
            backgroundColor: tintHex(color, 0.3, isDark),
            borderColor: color,
          },
    [showMedia, color, isDark],
  );
  // The expanded tile's corner scales with the responsive scale like the
  // rest of its px-fixed geometry — a fixed 20px corner on the 1.25× tablet
  // card read visibly tighter than the web render.
  const tileStyle = useMemo(
    () =>
      isExpandedGrid ? { borderRadius: Math.round(20 * scale), ...tileTint } : tileTint,
    [isExpandedGrid, scale, tileTint],
  );
  // What the panel's glass sees behind it (web fake-frost — see
  // CardBodyPanel's `cover` prop): the cover image, or the emoji tile's
  // tint pair. Native ignores this entirely.
  const panelCover = useMemo(
    () => (showMedia ? { image: image! } : { tint: tileTint }),
    [showMedia, image, tileTint],
  );

  // The tile's two chip slots — memoized for the same reason as the cover art
  // above: they live INSIDE the node that carries the press lift, and each one
  // is a `Badge`, i.e. another `MarqueeLabel`.
  const coverOverlayNode = useMemo(
    () =>
      coverOverlay ? (
        // Inside the tile ON PURPOSE: the chips ride the tile's hover/press
        // lift. 12px from the tile's top-left (5714:28959), × the responsive
        // scale on the expanded card like its other px-fixed geometry.
        <View
          className="absolute z-10 flex-row items-center gap-1"
          webProps={{ style: { top: scaled12, left: scaled12 } }}
          nativeProps={{
            // Raw 12 on compact for the same reason as the to-do chip's
            // corner: the tile's box is fixed px, so a scaled inset would
            // walk the chip inward as the ramp grew.
            style: {
              top: isCompactGrid ? 12 : scaled12,
              left: isCompactGrid ? 12 : scaled12,
            },
            pointerEvents: 'box-none',
          }}>
          {coverOverlay}
        </View>
      ) : null,
    [coverOverlay, isCompactGrid, scaled12],
  );
  const todoNode = useMemo(
    () => (
      /* Inside the tile for the same reason as `coverOverlay`: the to-do chip
         rides the tile's hover/press lift. 12px off the tile's top-right, ×
         the responsive scale like its left-hand sibling. */
      <CardTodoBadge
        count={todoCount}
        label={todoLabel}
        tooltip={todoTooltip}
        // Compact reads the raw 12: its box is fixed px, so a scaled corner
        // inset would walk the chip inward as the ramp grew. Expanded scales
        // with the box it hangs off, like every other offset on that card.
        top={isCompactGrid ? 12 : scaled12}
        right={isCompactGrid ? 12 : scaled12}
        pinned={isCompactGrid}
      />
    ),
    [todoCount, todoLabel, todoTooltip, isCompactGrid, scaled12],
  );

  // `as="div"` on web: the card contains a real <button> (the actions ghost
  // Button) — nesting it inside another <button> would be invalid HTML.
  const containerProps = {
    as: 'div',
    className: cn(
      deckCardVariants({ view: resolvedView, size: resolvedSize, media: showMedia }),
      DARK_LAYER1,
      className,
    ),
    onPress,
    onPressIn: handlePressIn,
    onPressOut: handlePressOut,
    onHoverIn: handleHoverIn,
    onHoverOut: handleHoverOut,
    // Light tick on touch-down alongside the press lift (native only).
    haptic: true,
    disabled,
    accessibilityRole: 'button',
    accessibilityLabel: accessibilityLabel ?? title,
    testID,
    // The surface goes on BOTH paths — web Pressable swallows `nativeProps`
    // entirely, so the native merge above never reaches it. The grid branch
    // overrides `webProps.style` with its box size, and spreads this first.
    webProps: {
      ...webProps,
      style: { ...surfaceStyle, ...(webProps as { style?: object } | undefined)?.style },
    },
    nativeProps: mergedNativeProps,
  } as const;

  if (resolvedView === 'list') {
    return (
      <Pressable {...containerProps}>
        {/* px-locked vertical gap — see cardVariants' list compounds: the
            row's height maths must not scale with the tablet rem ramp. */}
        <View className="min-w-0 flex-1 gap-[8px]">
          <View className="w-full flex-row items-center gap-2">
            <DeckIcon color={color} size={20} className="shrink-0" />
            {/* Title + trailing chip as one shrinkable group: 7px title→chip
                gap (spec), chip never shrinks — the title truncates. */}
            <View className="min-w-0 shrink flex-row items-center gap-[7px]">
              {titleNode}
              {titleTrailing ? <View className="shrink-0">{titleTrailing}</View> : null}
            </View>
          </View>
          {badgeRow}
        </View>
        {showMedia ? (
          // Thumbnail carries its own 4px frame around the 55×78 A4-format
          // image (Figma 5714:29717) and tucks toward the row's trailing
          // edge (closer to the `⋮` than the row gap alone would). px-locked:
          // the frame is part of the row's fixed 102px height maths.
          <View className="-mr-[8px] p-[4px]">
            <View className="h-[78px] w-[55px] overflow-hidden rounded-xl border border-secondary-200 web:shadow-card-cover">
              {imageNode}
            </View>
          </View>
        ) : null}
        {actions}
        <View className={deckCardBorderClass} />
      </Pressable>
    );
  }

  return (
    <Pressable
      {...containerProps}
      className={cn(containerProps.className, 'group/card')}
      webProps={{
        ...containerProps.webProps,
        style: { ...containerProps.webProps.style, ...gridBoxStyle },
      }}
      nativeProps={{
        ...containerProps.nativeProps,
        style: [containerProps.nativeProps.style, gridBoxStyle],
      }}>
      {/* Tile fills the whole inner box; the body panel overlays its bottom. */}
      <View
        className={cn(
          deckCardCoverVariants({ media: showMedia, size: resolvedSize }),
          // Pressed (touch) or pencil-hovered mirrors the web hover lift.
          // Explicit translate-y-0 when idle: NativeWind needs the
          // --tw-translate variables set from the first render (see
          // FolderTile's liftClasses note).
          lifted ? '-translate-y-1' : 'translate-y-0',
        )}
        webProps={tileStyle && { style: tileStyle }}
        nativeProps={tileStyle && { style: tileStyle }}>
        {showMedia ? imageNode : tagNode}
        {coverOverlayNode}
        {todoNode}
      </View>
      {/* Body panel over the tile, full card width (clipped to the card's
          28px radius at the bottom) — Figma "Card Body" + "Thumbnail". */}
      <View
        className={deckCardBodyVariants({ size: resolvedSize })}
        webProps={bodyStyle && { style: bodyStyle }}
        nativeProps={bodyStyle && { style: bodyStyle }}>
        <CardBodyPanel size={resolvedSize} cover={panelCover} />
        {isExpandedGrid ? (
          // Expanded panel anatomy (Figma 5656:17009): title at y=24 h=24
          // ending 8px before the ⋯ (right inset 16+32+8 = 56 = right-14),
          // ⋯ pinned at y=16 / right=16 in its OWN slot (rendered last so it
          // paints and receives touches on top), chips 16px off the left /
          // right / bottom edges. Absolute offsets × the responsive scale —
          // same rationale as FolderLikeCard's expanded panels.
          <>
            <View
              className="absolute left-4 right-14 flex-row items-center gap-2"
              webProps={{ style: { top: px(24), height: px(24) } }}
              nativeProps={{ style: { top: px(24), height: px(24) } }}>
              {titleNode}
              {titleTrailing}
            </View>
            <View
              className="absolute inset-x-4"
              webProps={{ style: { bottom: px(16) } }}
              nativeProps={{ style: { bottom: px(16) } }}>
              {badgeRow}
            </View>
            <View
              className="absolute right-4 flex-row items-center"
              webProps={{ style: { top: px(16), height: px(32) } }}
              nativeProps={{ style: { top: px(16), height: px(32) } }}>
              {actions}
            </View>
          </>
        ) : (
          // Compact panel (Figma 171:17544, 180×89): title row 32 tall at
          // y=14.5 from the panel's top edge, ending 8px before the ⋯; the ⋯
          // in its own slot at the same y, 16px off the right (rendered last
          // so it paints — and takes touches — above the title); chips
          // bottom-anchored at the DS 16px rule.
          //
          // Absolute, in px, for the reason `cardBodyVariants` spells out:
          // in flow the top gap was only ever the leftover slack, and the
          // tablet rem ramp ate all of it — the title ended up flush against
          // the panel's top edge. Absolute positioning also lets the rows
          // paint above the panel (which is itself absolute) without the
          // `relative` trick the flow version needed.
          <>
            <View
              className="absolute flex-row items-center"
              webProps={{ style: compactTitleRow }}
              nativeProps={{ style: compactTitleRow }}>
              {titleNode}
              {titleTrailing}
            </View>
            <View
              className="absolute"
              webProps={{ style: compactChipsRow }}
              nativeProps={{ style: compactChipsRow }}>
              {badgeRow}
            </View>
            <View
              className="absolute flex-row items-center"
              webProps={{ style: compactActionsSlot }}
              nativeProps={{ style: compactActionsSlot }}>
              {actions}
            </View>
          </>
        )}
      </View>
      {/* 1px border overlay on top — so the body panel above can span the
          full card width and align to it (no corner discontinuity). */}
      <View className={deckCardBorderClass} />
    </Pressable>
  );
}
/**
 * MEMOIZED, and that is the difference between a deck list that costs one render
 * per changed card and one that costs a full card subtree per visible card.
 *
 * These cards live in FlashLists whose hosts re-render for reasons no single
 * card can display — a store tick, a search keystroke, a sibling entering
 * selection mode. Unmemoized, each of those rebuilt every visible card's title
 * (a `MarqueeLabel`: measuring copy, gradient mask, `Animated.Value`), its chip
 * row (a `Badge` per activity) and its glass panel.
 *
 * The app tiles hand this stable identities for exactly that reason (`badges`
 * and `previews` out of `useMemo`, handlers out of `useCallback`, the to-do
 * chip as primitives). An inline `badges={[...]}` or an arrow `onPress` at a
 * call site silently turns the memo off — it does not break anything, it just
 * stops paying.
 */
export const DeckCard = memo(DeckCardImpl);
(DeckCard as { displayName?: string }).displayName = 'DeckCard';
